/*
 * Zarafae "Cut to Measure": product options state.
 *
 * Loaded with type="module" by blocks/zarafae-option.liquid, blocks/zarafae-size-chart.liquid and
 * blocks/zf-order-status.liquid. The browser runs a module URL once however many blocks print the
 * tag, and everything below is one-time document-level delegation behind a window flag, so theme
 * editor re-renders only need a refresh (shopify:section:load).
 *
 * The inline script in zarafae-option.liquid keeps the flows that must not change: the invalid
 * event errors and reveal, the sticky-bar capture, and the variant-change submit hold. This file:
 *  - shows measurements at the choice. The "Standard" pant length and "With sleeves" cells read the
 *    size chart block's JSON for the chosen sizes: the cell hint, the legend value and, only when
 *    the option opts in ("Add hint to order value"), the submitted line-item property;
 *  - writes the legend value ("Pant length: Standard, 40 in");
 *  - after a failed add to cart, fills the order status line above Add to cart
 *    ("Choose 2 more: Pant style, Sleeves"), each name a button to its group;
 *  - publishes zf:choices-changed on document with detail { missing: [labels], chosen: [values] }
 *    for the sticky bar. A listener that connects late dispatches zf:choices-request on document;
 *  - opens the size chart at the shopper's size.
 */

const OPTION = '[data-zf-option]';
const STATUS = '[data-zf-order-status]';
const CHOICES_CHANGED = 'zf:choices-changed';
const CHOICES_REQUEST = 'zf:choices-request';
const SVG_NS = 'http://www.w3.org/2000/svg';

/** Sections where an add to cart failed on missing choices; cleared once nothing is missing. */
const attempted = new WeakSet();
/** Parsed chart per script[data-zf-chart] node (a re-rendered section brings a new node). */
const chartCache = new WeakMap();
const queuedScopes = new Set();
let refreshQueued = false;

function reducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function closestIn(node, selector) {
  return node instanceof Element ? node.closest(selector) : null;
}

function scopeOf(node) {
  return closestIn(node, '.shopify-section') || document.body;
}

function groupsIn(scope) {
  return Array.from(scope.querySelectorAll(OPTION));
}

/** The checked radio, or the chosen <option> of a dropdown group. Both carry the data-zf-* attributes. */
function selectedControl(group) {
  const checked = group.querySelector('input:checked');
  if (checked) return checked;
  const select = group.querySelector('select');
  if (select && select.value) return select.options[select.selectedIndex] || null;
  return null;
}

/** The plain choice ("M", "Standard"). Lookups never use the submitted value, which may carry a hint. */
function choiceOf(control) {
  return control.getAttribute('data-zf-choice') || control.value || '';
}

function chosenFor(scope, role) {
  const group = scope.querySelector(`${OPTION}[data-zf-role="${role}"]`);
  const control = group ? selectedControl(group) : null;
  return control ? choiceOf(control) : '';
}

function pairs(list) {
  return Array.isArray(list) ? list.filter((entry) => Array.isArray(entry) && entry.length === 2) : [];
}

/**
 * JSON printed by zarafae-size-chart.liquid from its three tables:
 * { waist: [[size, value]], lengthSizes: [size], length: [[style, [value per lengthSizes]]], sleeve: [[size, value]] }
 */
function chartFor(scope) {
  const node = scope.querySelector('script[data-zf-chart]');
  if (!node) return null;
  if (chartCache.has(node)) return chartCache.get(node);

  let chart = null;
  try {
    const raw = JSON.parse(node.textContent || '{}');
    const sizes = Array.isArray(raw.lengthSizes) ? raw.lengthSizes : [];
    const length = new Map();
    pairs(raw.length).forEach(([style, values]) => {
      const bySize = new Map();
      if (Array.isArray(values)) {
        sizes.forEach((size, index) => {
          if (size && values[index]) bySize.set(size, values[index]);
        });
      }
      length.set(style, bySize);
    });
    chart = { waist: new Map(pairs(raw.waist)), length, sleeve: new Map(pairs(raw.sleeve)) };
  } catch (error) {
    chart = null;
  }

  chartCache.set(node, chart);
  return chart;
}

/** { pending: true } until the sizes it depends on are chosen, then { value } ('' when the chart has no match). */
function lookupMeasure(scope, group, chart) {
  const role = group.getAttribute('data-zf-role');
  let value;

  if (role === 'pant_length') {
    const size = chosenFor(scope, 'pant_size');
    const style = chosenFor(scope, 'pant_style');
    if (!size || !style) return { pending: true };
    const bySize = chart ? chart.length.get(style) : null;
    value = bySize ? bySize.get(size) : '';
  } else if (role === 'sleeves') {
    const size = chosenFor(scope, 'shirt_size');
    if (!size) return { pending: true };
    value = chart ? chart.sleeve.get(size) : '';
  } else {
    return null;
  }

  return { value: value || '' };
}

function applyMeasure(scope, group, chart) {
  const input = group.querySelector('input[data-zf-chart-cell]');
  if (!input) return;
  const result = lookupMeasure(scope, group, chart);
  if (!result) return;

  let measure = '';
  let hintText = input.getAttribute('data-zf-hint-fallback') || '';
  if (result.pending) {
    const pending = input.getAttribute('data-zf-hint-pending');
    if (pending) hintText = pending;
  } else if (result.value) {
    const format = group.getAttribute('data-zf-measure-format') || '[n]';
    measure = format.split('[n]').join(result.value);
    hintText = measure;
  }

  const hint = input.parentElement ? input.parentElement.querySelector('[data-zf-hint]') : null;
  if (hint && hint.textContent !== hintText) hint.textContent = hintText;

  if (measure) input.setAttribute('data-zf-measure', measure);
  else input.removeAttribute('data-zf-measure');

  // The submitted value changes only when the option opts in (data-zf-value-format is printed only
  // then). Until the client confirms the format, orders keep the plain choice.
  const choice = choiceOf(input);
  const valueFormat = group.getAttribute('data-zf-value-format');
  const value = valueFormat && measure ? valueFormat.split('[choice]').join(choice).split('[hint]').join(measure) : choice;
  if (input.value !== value) input.value = value;
}

function syncValue(group) {
  const output = group.querySelector('[data-zf-option-value]');
  if (!output) return;
  const control = selectedControl(group);
  let text = '';
  if (control) {
    const measure = control.getAttribute('data-zf-measure');
    text = `: ${choiceOf(control)}${measure ? `, ${measure}` : ''}`;
  }
  if (output.textContent !== text) output.textContent = text;
}

function stateOf(scope) {
  const missing = [];
  const chosen = [];
  const missingGroups = [];
  groupsIn(scope).forEach((group) => {
    const control = selectedControl(group);
    if (control) {
      // A waist-first pant size reads as its waist ("32 in"); every other group as its choice.
      chosen.push(control.getAttribute('data-zf-lead') || choiceOf(control));
      return;
    }
    if (group.hasAttribute('data-zf-required')) {
      missing.push(group.getAttribute('data-zf-label') || '');
      missingGroups.push(group);
    }
  });
  return { missing, chosen, missingGroups };
}

/** 16px ink square with a paper "!" drawn from two rects: straight lines, no colour. */
function alertIcon(className) {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', `zf-alert-icon ${className}`);
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  [
    ['0', '0', '16', '16', ''],
    ['7', '3', '2', '7', 'zf-alert-icon__mark'],
    ['7', '11', '2', '2', 'zf-alert-icon__mark'],
  ].forEach(([x, y, width, height, markClass]) => {
    const rect = document.createElementNS(SVG_NS, 'rect');
    rect.setAttribute('x', x);
    rect.setAttribute('y', y);
    rect.setAttribute('width', width);
    rect.setAttribute('height', height);
    if (markClass) rect.setAttribute('class', markClass);
    else rect.setAttribute('fill', 'currentColor');
    svg.append(rect);
  });
  return svg;
}

function renderStatus(scope, state) {
  const status = scope.querySelector(STATUS);
  const live = status ? status.querySelector('[data-zf-order-status-live]') : null;
  if (!live) return;

  if (!state.missing.length) attempted.delete(scope);
  const show = attempted.has(scope) && state.missing.length > 0;
  const template = status.getAttribute('data-zf-template') || '';
  const key = show ? `${template}|${state.missingGroups.map((group) => group.id).join('|')}|${state.missing.join('|')}` : '';

  // Rebuild only when the words change, so focus on a name button and the live region stay put.
  if (live.getAttribute('data-zf-key') === key) return;
  live.setAttribute('data-zf-key', key);

  if (!show) {
    live.replaceChildren();
    return;
  }

  const line = document.createElement('p');
  line.className = 'zf-order-status__line';
  line.append(alertIcon('zf-order-status__icon'));

  const text = document.createElement('span');
  text.className = 'zf-order-status__text';
  const lead = template.split('[n]').join(String(state.missing.length)).trim();
  if (lead) text.append(`${lead} `);

  state.missingGroups.forEach((group, index) => {
    if (index > 0) text.append(', ');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'zf-order-status__goto';
    button.setAttribute('data-zf-goto', group.id);
    button.textContent = state.missing[index];
    text.append(button);
  });

  line.append(text);
  live.replaceChildren(line);
}

function publish(state) {
  document.dispatchEvent(
    new CustomEvent(CHOICES_CHANGED, {
      detail: { missing: state.missing.slice(), chosen: state.chosen.slice() },
    })
  );
}

/** Size chart links printed by an option don't know the chart block's id; point them at the dialog in the same section. */
function linkChartTriggers(scope) {
  const dialog = scope.querySelector('dialog[data-zf-size-chart]');
  if (!dialog || !dialog.id) return;
  scope.querySelectorAll('[data-zf-size-chart-open]:not([aria-controls])').forEach((trigger) => {
    trigger.setAttribute('aria-controls', dialog.id);
  });
}

function refresh(scope) {
  if (!scope || !scope.isConnected) return;
  const groups = groupsIn(scope);
  if (!groups.length) return;

  const chart = chartFor(scope);
  groups.forEach((group) => applyMeasure(scope, group, chart));
  groups.forEach(syncValue);
  linkChartTriggers(scope);

  const state = stateOf(scope);
  renderStatus(scope, state);
  publish(state);
}

function refreshAll() {
  const scopes = new Set();
  document.querySelectorAll(OPTION).forEach((group) => scopes.add(scopeOf(group)));
  scopes.forEach(refresh);
}

/** One click can fire "invalid" several times (Horizon's checkValidity, then native submit validation). */
function queueRefresh(scope) {
  queuedScopes.add(scope);
  if (refreshQueued) return;
  refreshQueued = true;
  setTimeout(() => {
    refreshQueued = false;
    const scopes = Array.from(queuedScopes);
    queuedScopes.clear();
    scopes.forEach(refresh);
  }, 0);
}

function reveal(group) {
  const control = group.querySelector('input:checked') || group.querySelector('input, select');
  if (typeof group.scrollIntoView === 'function') {
    group.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
  }
  if (!control || typeof control.focus !== 'function') return;
  try {
    control.focus({ preventScroll: true });
  } catch (error) {
    control.focus();
  }
}

/**
 * Opens the chart at the shopper's sizes, the pant style underlined.
 * - Waist and length (table 1, table 2, the phone table's rows) follow the pant size, or the shirt size
 *   while pant size is empty.
 * - Sleeve length (table 3's rows, the phone table's Sleeve cells) follows the shirt size, or the pant
 *   size while shirt size is empty: the same size the Sleeves option measures from, so the chart never
 *   marks another size's sleeve when the two sizes differ.
 */
function highlightChart(dialog) {
  const scope = scopeOf(dialog);
  const pantSize = chosenFor(scope, 'pant_size');
  const shirtSize = chosenFor(scope, 'shirt_size');
  const size = pantSize || shirtSize;
  const sleeveSize = shirtSize || pantSize;
  const style = chosenFor(scope, 'pant_style');

  dialog.querySelectorAll('.is-zf-size, .is-zf-style, .is-zf-sleeve').forEach((node) => {
    node.classList.remove('is-zf-size', 'is-zf-style', 'is-zf-sleeve');
  });

  if (size) {
    dialog.querySelectorAll('[data-zf-size]').forEach((node) => {
      if (node.getAttribute('data-zf-size') === size) node.classList.add('is-zf-size');
    });
  }

  if (sleeveSize) {
    dialog.querySelectorAll('[data-zf-sizes]').forEach((node) => {
      const sizes = (node.getAttribute('data-zf-sizes') || '').split('|').map((entry) => entry.trim());
      if (sizes.includes(sleeveSize)) node.classList.add('is-zf-size');
    });
    dialog.querySelectorAll('[data-zf-sleeve-size]').forEach((node) => {
      if (node.getAttribute('data-zf-sleeve-size') === sleeveSize) node.classList.add('is-zf-sleeve');
    });
  }

  if (style) {
    dialog.querySelectorAll('[data-zf-style]').forEach((node) => {
      if (node.getAttribute('data-zf-style') === style) node.classList.add('is-zf-style');
    });
  }

  // The model note is written once, on the Shirt size option; the sheet repeats it under its title.
  const note = dialog.querySelector('[data-zf-chart-model-note]');
  if (note) {
    const source = scope.querySelector('[data-zf-model-note]');
    const text = source ? source.textContent.trim() : '';
    note.textContent = text;
    note.hidden = !text;
  }

  // Only the table showing at this width has client rects (the other is display: none).
  const target = Array.from(dialog.querySelectorAll('tr.is-zf-size, th.is-zf-size')).find(
    (node) => node.getClientRects().length > 0
  );
  if (target) target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

/** The Order status block itself. A parent block that merely contains it (Details, Buy buttons) never previews the line. */
function statusFrom(node) {
  return node instanceof Element ? node.closest(STATUS) : null;
}

function init() {
  document.addEventListener('change', (event) => {
    const group = closestIn(event.target, OPTION);
    if (group) refresh(scopeOf(group));
  });

  // Capture: "invalid" doesn't bubble. Any invalid option control means an add to cart just failed.
  document.addEventListener(
    'invalid',
    (event) => {
      const group = closestIn(event.target, OPTION);
      if (!group) return;
      const scope = scopeOf(group);
      attempted.add(scope);
      queueRefresh(scope);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const button = closestIn(event.target, '[data-zf-goto]');
    if (!button) return;
    const group = document.getElementById(button.getAttribute('data-zf-goto') || '');
    if (group) reveal(group);
  });

  // Dispatched by zarafae-size-chart.liquid right after showModal().
  document.addEventListener('zf:size-chart-open', (event) => {
    if (event.target instanceof Element) highlightChart(event.target);
  });

  document.addEventListener(CHOICES_REQUEST, refreshAll);

  // A Dupatta change that loads a new product morphs <main>; re-apply once the fetch settles.
  document.addEventListener(
    'shopify:product:select',
    (event) => {
      const promise = event.promise;
      if (!promise || typeof promise.then !== 'function') return;
      const settle = () => setTimeout(refreshAll, 0);
      promise.then(settle, settle);
    },
    true
  );

  window.addEventListener('pageshow', refreshAll);
  document.addEventListener('shopify:section:load', refreshAll);

  // Theme editor: selecting the Order status block previews the line with the current gaps.
  document.addEventListener('shopify:block:select', (event) => {
    const status = statusFrom(event.target);
    if (!status) return;
    const scope = scopeOf(status);
    attempted.add(scope);
    refresh(scope);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const status = statusFrom(event.target);
    if (!status) return;
    const scope = scopeOf(status);
    attempted.delete(scope);
    refresh(scope);
  });

  refreshAll();
}

if (!window.__zfProductOptionsInit) {
  window.__zfProductOptionsInit = true;
  init();
}
